import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../../stores/authStore';
import { emitToast } from '../../lib/toastEvents';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = useAuthStore.getState();
    const token = state.accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add organization context header for multi-tenancy. Super-admin
    // impersonation overrides the home org with the target org's id and
    // adds an explicit `X-Impersonating-Org-Id` so the server can apply
    // the read-only middleware.
    if (config.headers) {
      const impersonating = state.impersonatingOrg;
      if (impersonating) {
        config.headers['X-Organization-ID'] = impersonating.id;
        config.headers['X-Impersonating-Org-Id'] = impersonating.id;
      } else {
        const organizationId = state.user?.organization_id;
        if (organizationId) {
          config.headers['X-Organization-ID'] = organizationId;
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized - but skip for login/auth endpoints
    if (error.response?.status === 401 && originalRequest) {
      // Skip token refresh for login endpoints
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      // Check if this is already a retry
      if ((originalRequest as any)._retry) {
        // Clear auth and redirect to login
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      (originalRequest as any)._retry = true;
      
      // Try to refresh token
      try {
        await useAuthStore.getState().refreshAccessToken();
        // Retry original request with new token
        const newToken = useAuthStore.getState().accessToken;
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    // Handle other errors
    if (error.response?.status === 402) {
      // Payment Required — subscription tier gate hit server-side
      const data = error.response.data as { detail?: unknown } | undefined;
      const detail =
        typeof data?.detail === 'string'
          ? data.detail
          : 'This feature requires a higher subscription tier.';
      emitToast({
        type: 'warning',
        title: 'Upgrade required',
        message: detail,
      });
    } else if (error.response?.status === 403) {
      // Permission denied
      console.error('Permission denied:', error.response.data);
    } else if (error.response?.status === 404) {
      // Not found
      console.error('Resource not found:', error.response.data);
    } else if (error.response?.status === 422) {
      // Validation error
      console.error('Validation error:', error.response.data);
    } else if (error.response?.status === 500) {
      // Server error
      console.error('Server error:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// Helper to extract error message
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    if (error.response?.data?.detail) {
      // FastAPI error format
      if (typeof error.response.data.detail === 'string') {
        return error.response.data.detail;
      }
      // Validation errors
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail
          .map((err: any) => err.msg)
          .join(', ');
      }
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper for form-encoded requests (used for login)
export const apiClientFormEncoded = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  timeout: 30000,
});

// Add same interceptors to form-encoded client
apiClientFormEncoded.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = useAuthStore.getState();
    const token = state.accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.headers) {
      const impersonating = state.impersonatingOrg;
      if (impersonating) {
        config.headers['X-Organization-ID'] = impersonating.id;
        config.headers['X-Impersonating-Org-Id'] = impersonating.id;
      } else {
        const organizationId = state.user?.organization_id;
        if (organizationId) {
          config.headers['X-Organization-ID'] = organizationId;
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClientFormEncoded.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized - but skip for login/auth endpoints
    if (error.response?.status === 401 && originalRequest) {
      // Skip token refresh for login endpoints
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      // Check if this is already a retry
      if ((originalRequest as any)._retry) {
        // Clear auth and redirect to login
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      (originalRequest as any)._retry = true;
      
      // Try to refresh token
      try {
        await useAuthStore.getState().refreshAccessToken();
        // Retry original request with new token
        const newToken = useAuthStore.getState().accessToken;
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return apiClientFormEncoded(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect
        useAuthStore.getState().clearAuth();
        if (typeof window !== 'undefined' && window.location && !import.meta.env.TEST) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
